import React, { useState } from 'react';
import { motion } from 'framer-motion';
import { MapPin, Phone, Mail, Clock, Send } from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { PageSEO } from '../components/PageSEO';

const API_URL = process.env.REACT_APP_BACKEND_URL;

export const ContactPage = () => {
  const [loading, setLoading] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    phone: '',
    subject: '',
    message: ''
  });

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);
    
    try {
      await axios.post(`${API_URL}/api/contact`, form);
      toast.success('Mesajınız başarıyla gönderildi!');
      setForm({ name: '', email: '', phone: '', subject: '', message: '' });
    } catch (error) {
      toast.error('Mesaj gönderilirken bir hata oluştu.');
    }
    
    setLoading(false);
  };

  const contactInfo = [
    {
      icon: MapPin,
      title: 'Adres',
      content: 'Örnek Mahallesi, Doğa Sokak No: 12\nKocaeli, Türkiye'
    },
    {
      icon: Phone,
      title: 'Telefon',
      content: '+90 262 123 45 67'
    },
    {
      icon: Mail,
      title: 'E-posta',
      content: 'info@kosad.org'
    },
    {
      icon: Clock,
      title: 'Çalışma Saatleri',
      content: 'Pazartesi - Cuma: 09:00 - 18:00\nCumartesi: 10:00 - 14:00'
    }
  ];

  return (
    <div data-testid="contact-page">
      <PageSEO title="İletişim" description="KOSAD ile iletişime geçin. Adres, telefon, e-posta ve iletişim formu. Kocaeli merkezimizi ziyaret edin." path="/iletisim" />
      {/* Hero */}
      <section className="relative py-32 bg-[#1A3625]">
        <div className="container-custom">
          <motion.div 
            className="max-w-3xl"
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
          >
            <span className="label-uppercase text-[#D4AF37] mb-4 block">İletişim</span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl text-white font-semibold mb-6">
              Bize Ulaşın
            </h1>
            <p className="text-base text-white/80">
              Sorularınız, önerileriniz veya işbirliği teklifleriniz için bize ulaşabilirsiniz.
            </p>
          </motion.div>
        </div>
      </section>

      <section className="section-padding">
        <div className="container-custom">
          <div className="grid lg:grid-cols-2 gap-16">
            {/* Contact Info */}
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <h2 className="text-xl md:text-2xl text-[#1A3625] mb-8">İletişim Bilgileri</h2>
              
              <div className="space-y-6 mb-12">
                {contactInfo.map((info, index) => (
                  <div key={index} className="flex gap-4" data-testid={`contact-info-${index}`}>
                    <div className="w-12 h-12 bg-[#1A3625]/10 rounded-2xl flex items-center justify-center flex-shrink-0">
                      <info.icon className="w-6 h-6 text-[#1A3625]" />
                    </div>
                    <div>
                      <h3 className="font-medium text-[#1A3625] mb-1">{info.title}</h3>
                      <p className="text-[#5C5C5C] whitespace-pre-line">{info.content}</p>
                    </div>
                  </div>
                ))}
              </div>

              {/* Map */}
              <div className="rounded-3xl overflow-hidden h-80" data-testid="map-container">
                <iframe
                  src="https://www.google.com/maps/embed?pb=!1m18!1m12!1m3!1d192697.3726135!2d29.7!3d40.76!2m3!1f0!2f0!3f0!3m2!1i1024!2i768!4f13.1!3m3!1m2!1s0x14cb4e7e6e7b5c5d%3A0x5f7b0c4c5f8e0e0e!2sKocaeli%2C%20Turkey!5e0!3m2!1sen!2str!4v1234567890"
                  width="100%"
                  height="100%"
                  style={{ border: 0 }}
                  allowFullScreen=""
                  loading="lazy"
                  referrerPolicy="no-referrer-when-downgrade"
                  title="KOSAD Konum"
                />
              </div>
            </motion.div>

            {/* Contact Form */}
            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
            >
              <div className="card-premium">
                <h2 className="text-xl md:text-2xl text-[#1A3625] mb-8">Mesaj Gönderin</h2>
                
                <form onSubmit={handleSubmit} className="space-y-6" data-testid="contact-form">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-base font-medium text-[#1A3625] mb-2">Ad Soyad *</label>
                      <input
                        type="text"
                        value={form.name}
                        onChange={(e) => setForm({...form, name: e.target.value})}
                        className="input-premium"
                        required
                        data-testid="contact-name"
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-[#1A3625] mb-2">E-posta *</label>
                      <input
                        type="email"
                        value={form.email}
                        onChange={(e) => setForm({...form, email: e.target.value})}
                        className="input-premium"
                        required
                        data-testid="contact-email"
                      />
                    </div>
                  </div>
                  
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-base font-medium text-[#1A3625] mb-2">Telefon</label>
                      <input
                        type="tel"
                        value={form.phone}
                        onChange={(e) => setForm({...form, phone: e.target.value})}
                        className="input-premium"
                        data-testid="contact-phone"
                      />
                    </div>
                    <div>
                      <label className="block text-base font-medium text-[#1A3625] mb-2">Konu *</label>
                      <select
                        value={form.subject}
                        onChange={(e) => setForm({...form, subject: e.target.value})}
                        className="input-premium"
                        required
                        data-testid="contact-subject"
                      >
                        <option value="">Seçiniz</option>
                        <option value="general">Genel Bilgi</option>
                        <option value="membership">Üyelik</option>
                        <option value="partnership">İş Birliği</option>
                        <option value="market">Market / Sipariş</option>
                        <option value="other">Diğer</option>
                      </select>
                    </div>
                  </div>
                  
                  <div>
                    <label className="block text-base font-medium text-[#1A3625] mb-2">Mesajınız *</label>
                    <textarea
                      value={form.message}
                      onChange={(e) => setForm({...form, message: e.target.value})}
                      className="input-premium min-h-[150px]"
                      required
                      data-testid="contact-message"
                    />
                  </div>
                  
                  <button
                    type="submit"
                    disabled={loading}
                    className="btn-primary w-full flex items-center justify-center gap-2"
                    data-testid="contact-submit"
                  >
                    {loading ? 'Gönderiliyor...' : (
                      <>
                        <Send className="w-5 h-5" />
                        Mesaj Gönder
                      </>
                    )}
                  </button>
                </form>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Business Collaboration */}
      <section className="section-padding bg-[#D4AF37]">
        <div className="container-custom text-center">
          <h2 className="text-3xl md:text-4xl text-[#1A3625] mb-4">
            Kurumsal İşbirliği
          </h2>
          <p className="text-[#1A3625]/80 mb-8 max-w-xl mx-auto">
            Şirketiniz için sürdürülebilirlik projelerinde ortaklık kurmak mı istiyorsunuz?
          </p>
          <a href="/katil" className="btn-primary">
            İşbirliği Başvurusu
          </a>
        </div>
      </section>
    </div>
  );
};
